// =============================================================================
// Repository: Superadmin - all platform-level data access
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

// ─── SUPERADMIN CHECK ──────────────────────────────────────────────────────

export async function checkIsSuperadmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_superadmin');
  if (error) return false;
  return !!data;
}

// ---- Types ----

export interface PlatformStats {
  totalOrganizations: number;
  totalUsers: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalProjects: number;
  totalTasks: number;
  totalSpaces: number;
  totalMembers: number;
  recentSignups7d: number;
}

export interface RecentOrg {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  member_count: number;
}

export interface RecentUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
}

export interface SuperadminOrganization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  logo_url: string | null;
  member_count: number;
  project_count: number;
  space_count: number;
}

export interface OrgMemberDetail {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  role_code: string | null;
}

export interface OrgProjectDetail {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  task_count: number;
}

export interface OrgSpaceDetail {
  id: string;
  name: string;
  visibility: string;
  is_archive: boolean;
  created_at: string;
}

export interface SuperadminUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  consent?: { marketing_consent: boolean; terms_consent: boolean; created_at: string } | null;
  onboarding?: {
    usage_context: string | null;
    primary_goal: string | null;
    team_size: string | null;
    discovery_sources: string[] | null;
    invited_emails: string[] | null;
    completed_at: string | null;
    skipped_at: string | null;
  } | null;
}

export interface SuperadminEntry {
  id: string;
  email: string;
  created_at: string;
}

export interface SuperadminAuditLog {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  user_id: string | null;
  created_at: string;
  org_name: string;
  user_email: string | null;
}

export interface PlatformPlan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  max_members: number | null;
  max_projects: number | null;
  features: unknown;
  is_active: boolean;
  sort_order: number;
}

export interface PlatformSubscription {
  id: string;
  status: string;
  billing_cycle: string;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string;
  org_name: string;
  plan_name: string;
}

// ---- Dashboard ----

export async function fetchPlatformStats(): Promise<{
  stats: PlatformStats;
  recentOrgs: RecentOrg[];
  recentUsers: RecentUser[];
}> {
  const [
    { count: orgCount },
    { count: userCount },
    { count: subCount },
    { count: activeSubCount },
    { count: projectCount },
    { count: taskCount },
    { count: spaceCount },
    { count: memberCount },
    { data: recentOrgsData },
    { data: recentUsersData },
  ] = await Promise.all([
    supabase.from('core_organizations').select('*', { count: 'exact', head: true }),
    supabase.from('core_profiles').select('*', { count: 'exact', head: true }),
    supabase.from('platform_subscriptions').select('*', { count: 'exact', head: true }),
    supabase.from('platform_subscriptions').select('*', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('project_projects').select('*', { count: 'exact', head: true }),
    supabase.from('project_tasks').select('*', { count: 'exact', head: true }),
    supabase.from('project_spaces').select('*', { count: 'exact', head: true }),
    supabase.from('core_organization_members').select('*', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('core_organizations').select('id, name, slug, created_at').order('created_at', { ascending: false }).limit(5),
    supabase.from('core_profiles').select('id, email, full_name, avatar_url, created_at').order('created_at', { ascending: false }).limit(5),
  ]);

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const { count: recentSignupsCount } = await supabase
    .from('core_profiles')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', sevenDaysAgo.toISOString());

  const orgsWithCounts = await Promise.all(
    (recentOrgsData || []).map(async (org) => {
      const { count } = await supabase
        .from('core_organization_members')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', org.id)
        .eq('status', 'active');
      return { ...org, member_count: count || 0 };
    })
  );

  return {
    stats: {
      totalOrganizations: orgCount || 0,
      totalUsers: userCount || 0,
      totalSubscriptions: subCount || 0,
      activeSubscriptions: activeSubCount || 0,
      totalProjects: projectCount || 0,
      totalTasks: taskCount || 0,
      totalSpaces: spaceCount || 0,
      totalMembers: memberCount || 0,
      recentSignups7d: recentSignupsCount || 0,
    },
    recentOrgs: orgsWithCounts,
    recentUsers: recentUsersData || [],
  };
}

// ---- Organizations ----

export async function fetchSuperadminOrganizations(): Promise<SuperadminOrganization[]> {
  const { data, error } = await supabase
    .from('core_organizations')
    .select('id, name, slug, created_at, logo_url')
    .order('created_at', { ascending: false });
  if (error) throw error;

  return Promise.all(
    (data || []).map(async (org) => {
      const [{ count: mc }, { count: pc }, { count: sc }] = await Promise.all([
        supabase.from('core_organization_members').select('*', { count: 'exact', head: true }).eq('org_id', org.id).eq('status', 'active'),
        supabase.from('project_projects').select('*', { count: 'exact', head: true }).eq('org_id', org.id),
        supabase.from('project_spaces').select('*', { count: 'exact', head: true }).eq('org_id', org.id),
      ]);
      return { ...org, member_count: mc || 0, project_count: pc || 0, space_count: sc || 0 };
    })
  );
}

export async function fetchOrgDetail(orgId: string): Promise<{
  members: OrgMemberDetail[];
  projects: OrgProjectDetail[];
  spaces: OrgSpaceDetail[];
}> {
  const { data: membersData } = await supabase
    .from('core_organization_members')
    .select(`id, user_id, status, created_at, profile:core_profiles(full_name, email, avatar_url)`)
    .eq('org_id', orgId)
    .order('created_at');

  const membersWithRoles = await Promise.all(
    (membersData || []).map(async (m: any) => {
      const { data: roleData } = await supabase
        .from('core_member_roles')
        .select('role:core_roles(code)')
        .eq('member_id', m.id)
        .limit(1)
        .maybeSingle();
      return {
        id: m.id,
        user_id: m.user_id,
        status: m.status,
        created_at: m.created_at,
        full_name: m.profile?.full_name || null,
        email: m.profile?.email || '',
        avatar_url: m.profile?.avatar_url || null,
        role_code: (roleData as any)?.role?.code || null,
      };
    })
  );

  const { data: projectsData } = await supabase
    .from('project_projects')
    .select('id, name, description, created_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  const projectsWithCounts = await Promise.all(
    (projectsData || []).map(async (p) => {
      const { count } = await supabase
        .from('project_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', p.id);
      return { ...p, task_count: count || 0 };
    })
  );

  const { data: spacesData } = await supabase
    .from('project_spaces')
    .select('id, name, visibility, is_archive, created_at')
    .eq('org_id', orgId)
    .order('created_at');

  return {
    members: membersWithRoles,
    projects: projectsWithCounts,
    spaces: spacesData || [],
  };
}

// ---- Users ----

export async function fetchSuperadminUsers(): Promise<SuperadminUser[]> {
  const { data, error } = await supabase.rpc('superadmin_users_overview' as any);
  if (error) throw error;
  return (data as unknown as SuperadminUser[]) || [];
}

// ---- Superadmins ----

export async function fetchSuperadmins(): Promise<SuperadminEntry[]> {
  const { data, error } = await supabase
    .from('platform_superadmins')
    .select('id, email, created_at')
    .order('created_at');
  if (error) throw error;
  return data || [];
}

export async function addSuperadmin(email: string): Promise<void> {
  const { error } = await supabase
    .from('platform_superadmins')
    .insert({ email: email.trim().toLowerCase() });
  if (error) {
    if (error.code === '23505') throw new Error('Cet email est déjà superadmin');
    throw error;
  }
}

export async function removeSuperadmin(id: string): Promise<void> {
  const { error } = await supabase
    .from('platform_superadmins')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---- Audit Logs ----

export async function fetchSuperadminAuditLogs(): Promise<SuperadminAuditLog[]> {
  const { data, error } = await supabase
    .from('core_audit_logs')
    .select(`id, action, entity_type, entity_id, user_id, created_at, org:core_organizations(name)`)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;

  const userIds = [...new Set((data || []).map((d) => d.user_id).filter(Boolean))];
  const { data: profiles } = await supabase
    .from('core_profiles')
    .select('id, email')
    .in('id', userIds as string[]);

  const profileMap = new Map((profiles || []).map((p) => [p.id, p.email]));

  return (data || []).map((log: any) => ({
    id: log.id,
    action: log.action,
    entity_type: log.entity_type,
    entity_id: log.entity_id,
    user_id: log.user_id,
    created_at: log.created_at,
    org_name: log.org?.name || 'N/A',
    user_email: log.user_id ? profileMap.get(log.user_id) || null : null,
  }));
}

// ---- Plans ----

export async function fetchPlatformPlans(): Promise<PlatformPlan[]> {
  const { data, error } = await supabase
    .from('platform_plans')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

// ---- Subscriptions ----

export async function fetchPlatformSubscriptions(): Promise<PlatformSubscription[]> {
  const { data, error } = await supabase
    .from('platform_subscriptions')
    .select(`id, status, billing_cycle, current_period_start, current_period_end, created_at, org:core_organizations(name), plan:platform_plans(name)`)
    .order('created_at', { ascending: false });
  if (error) throw error;

  return (data || []).map((sub: any) => ({
    id: sub.id,
    status: sub.status,
    billing_cycle: sub.billing_cycle,
    current_period_start: sub.current_period_start,
    current_period_end: sub.current_period_end,
    created_at: sub.created_at,
    org_name: sub.org?.name || 'N/A',
    plan_name: sub.plan?.name || 'N/A',
  }));
}
